const BASE: string =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ??
  "http://localhost:8787/api";

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown, msg: string) {
    super(msg);
    this.status = status;
    this.body = body;
  }
}

async function request<T>(
  path: string,
  init?: RequestInit & { auth?: boolean },
): Promise<T> {
  const headers: Record<string, string> = {
    "content-type": "application/json",
    ...(init?.headers as Record<string, string> | undefined),
  };
  // Auth now travels in an httpOnly cookie the browser attaches automatically;
  // `credentials: "include"` is required for that cookie to be sent cross-site.
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers,
    credentials: "include",
  });
  let body: unknown = null;
  const text = await res.text();
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = text;
    }
  }
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    if (
      body &&
      typeof body === "object" &&
      typeof (body as { error?: string }).error === "string"
    ) {
      msg = (body as { error: string }).error;
    }
    throw new ApiError(res.status, body, msg);
  }
  return body as T;
}

export const api = {
  get: <T>(p: string) => request<T>(p, { method: "GET" }),
  post: <T>(p: string, b?: unknown, auth = true) =>
    request<T>(p, {
      method: "POST",
      body: b ? JSON.stringify(b) : undefined,
      auth,
    }),
  put: <T>(p: string, b?: unknown) =>
    request<T>(p, { method: "PUT", body: b ? JSON.stringify(b) : undefined }),
  patch: <T>(p: string, b?: unknown) =>
    request<T>(p, { method: "PATCH", body: b ? JSON.stringify(b) : undefined }),
  del: <T>(p: string, b?: unknown) =>
    request<T>(p, {
      method: "DELETE",
      body: b ? JSON.stringify(b) : undefined,
    }),
};

export interface User {
  id: number;
  username: string;
  email: string;
  name: string;
  roles: string[];
  permissions: string[];
}

export function getApiBase(): string {
  return BASE;
}

export async function checkApiHealth(): Promise<{
  ok: boolean;
  error?: string;
}> {
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 5000);
    const resp = await fetch(`${BASE}/health`, { signal: controller.signal });
    clearTimeout(timer);
    if (!resp.ok) return { ok: false, error: `Server returned ${resp.status}` };
    return { ok: true };
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : "Cannot reach server";
    return { ok: false, error: message };
  }
}
